/**
 * Glyph layout over MSDF font metrics: greedy word wrap (char-break for over-long words),
 * per-line alignment, kerning + letter-spacing. Output is positioned glyph quads in LOGICAL px
 * relative to the text block's top-left, plus the measured block size — used both by the Yoga
 * measure fn (measure-only) and the paint walk (quads).
 */
import type { TextAlign } from '../types';
import { MsdfFontData } from './MsdfFont';

export interface GlyphQuad {
  x: number;
  y: number;
  w: number;
  h: number;
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}

export interface TextLayoutOptions {
  fontSize: number;
  /** Multiplier of fontSize (line advance = fontSize × lineHeight). */
  lineHeight: number;
  letterSpacing: number;
  align: TextAlign;
  /** Wrap width in logical px; Infinity = no wrap. */
  maxWidth: number;
  /** `white-space: nowrap` = false — explicit \n still breaks, greedy wrap doesn't (default true). */
  wrap?: boolean;
  /** `text-overflow: ellipsis` — truncate lines overflowing maxWidth with '…' (or '...'). */
  ellipsis?: boolean;
  /** Skip quad generation (measure-only fast path). */
  measureOnly?: boolean;
}

export interface TextLayout {
  width: number;
  height: number;
  quads: GlyphQuad[];
}

interface Word {
  codepoints: number[];
  width: number;
}

function wordWidth(font: MsdfFontData, cps: number[], fontSize: number, letterSpacing: number): number {
  let w = 0;
  for (let i = 0; i < cps.length; i++) {
    const g = font.glyph(cps[i]!);
    if (!g) continue;
    w += g.advance * fontSize + letterSpacing;
    if (i + 1 < cps.length) w += font.kerning(cps[i]!, cps[i + 1]!) * fontSize;
  }
  return w;
}

export function layoutText(font: MsdfFontData, text: string, opts: TextLayoutOptions): TextLayout {
  const { fontSize, letterSpacing, maxWidth } = opts;
  const wrap = opts.wrap !== false;
  const lineAdvance = fontSize * opts.lineHeight;
  const spaceGlyph = font.glyph(0x20);
  const spaceWidth = (spaceGlyph?.advance ?? 0.25) * fontSize + letterSpacing;

  // split into lines (explicit \n), then words
  const lines: { words: Word[]; width: number }[] = [];
  for (const rawLine of text.split('\n')) {
    const words: Word[] = [];
    for (const token of rawLine.split(' ')) {
      const cps = [...token].map((c) => c.codePointAt(0)!);
      if (!cps.length) {
        words.push({ codepoints: [], width: 0 }); // consecutive spaces
        continue;
      }
      const w = wordWidth(font, cps, fontSize, letterSpacing);
      if (wrap && w > maxWidth && cps.length > 1) {
        // over-long word: char-break into fitting chunks
        let chunk: number[] = [];
        for (const cp of cps) {
          const tryChunk = [...chunk, cp];
          if (chunk.length && wordWidth(font, tryChunk, fontSize, letterSpacing) > maxWidth) {
            words.push({ codepoints: chunk, width: wordWidth(font, chunk, fontSize, letterSpacing) });
            chunk = [cp];
          } else {
            chunk = tryChunk;
          }
        }
        if (chunk.length) words.push({ codepoints: chunk, width: wordWidth(font, chunk, fontSize, letterSpacing) });
      } else {
        words.push({ codepoints: cps, width: w });
      }
    }

    // greedy wrap into visual lines (nowrap: the raw line stays whole)
    let cur: Word[] = [];
    let curWidth = 0;
    const flush = () => {
      lines.push({ words: cur, width: curWidth });
      cur = [];
      curWidth = 0;
    };
    for (const word of words) {
      const extra = (cur.length ? spaceWidth : 0) + word.width;
      if (wrap && cur.length && curWidth + extra > maxWidth) flush();
      curWidth += (cur.length ? spaceWidth : 0) + word.width;
      cur.push(word);
    }
    flush();
  }

  // text-overflow: ellipsis — collapse overflowing lines to `<prefix>…` fitting maxWidth
  if (opts.ellipsis && Number.isFinite(maxWidth)) {
    const ellCps = font.glyph(0x2026) ? [0x2026] : [0x2e, 0x2e, 0x2e];
    const ellWidth = wordWidth(font, ellCps, fontSize, letterSpacing);
    for (const line of lines) {
      if (line.width <= maxWidth) continue;
      // flatten with explicit space codepoints (space advances but emits no quad)
      const flat: number[] = [];
      for (let i = 0; i < line.words.length; i++) {
        if (i > 0) flat.push(0x20);
        flat.push(...line.words[i]!.codepoints);
      }
      let keep = flat.length;
      while (keep > 0 && wordWidth(font, flat.slice(0, keep), fontSize, letterSpacing) + ellWidth > maxWidth) {
        keep--;
      }
      const cps = [...flat.slice(0, keep), ...ellCps];
      const width = wordWidth(font, cps, fontSize, letterSpacing);
      line.words = [{ codepoints: cps, width }];
      line.width = width;
    }
  }

  const blockWidth = lines.reduce((m, l) => Math.max(m, l.width), 0);
  const blockHeight = lines.length * lineAdvance;
  const layout: TextLayout = { width: blockWidth, height: blockHeight, quads: [] };
  if (opts.measureOnly) return layout;

  const alignWidth = Number.isFinite(maxWidth) ? Math.max(maxWidth, blockWidth) : blockWidth;
  const { atlasWidth, atlasHeight, yOriginBottom } = font;

  for (let li = 0; li < lines.length; li++) {
    const line = lines[li]!;
    const baseline = li * lineAdvance + font.ascender * fontSize;
    let penX = opts.align === 'center' ? (alignWidth - line.width) / 2 : opts.align === 'right' ? alignWidth - line.width : 0;

    for (let wi = 0; wi < line.words.length; wi++) {
      const cps = line.words[wi]!.codepoints;
      for (let i = 0; i < cps.length; i++) {
        const g = font.glyph(cps[i]!);
        if (!g) continue;
        if (g.plane && g.atlas) {
          const x = penX + g.plane.left * fontSize;
          const yTop = baseline - g.plane.top * fontSize;
          const w = (g.plane.right - g.plane.left) * fontSize;
          const h = (g.plane.top - g.plane.bottom) * fontSize;
          const u0 = g.atlas.left / atlasWidth;
          const u1 = g.atlas.right / atlasWidth;
          // UV V: -yorigin top (yOriginBottom=false) atlasBounds are top-left pixel coords that match the
          // top-left texture upload directly (V = y/H); -yorigin bottom needs the `1 - y/H` flip. Glyph
          // vertical ORIENTATION is handled by the Y-down→Y-up plane normalization in MsdfFontData.parse.
          const v0 = yOriginBottom ? 1 - g.atlas.top / atlasHeight : g.atlas.top / atlasHeight;
          const v1 = yOriginBottom ? 1 - g.atlas.bottom / atlasHeight : g.atlas.bottom / atlasHeight;
          layout.quads.push({ x, y: yTop, w, h, u0, v0, u1, v1 });
        }
        penX += g.advance * fontSize + letterSpacing;
        if (i + 1 < cps.length) penX += font.kerning(cps[i]!, cps[i + 1]!) * fontSize;
      }
      if (wi + 1 < line.words.length) penX += spaceWidth;
    }
  }
  return layout;
}
